import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import type { ApiEnvelope } from '@/types'

export interface ApiConfig {
  cloudUrl: string
  adminKey: string
}

/**
 * The console has no login. Every admin call is authorised by the shared
 * `X-Admin-Key` header, which must match ADMIN_API_KEY on the server.
 */
let cached: { signature: string; client: AxiosInstance } | null = null

function client({ cloudUrl, adminKey }: ApiConfig): AxiosInstance {
  const signature = `${cloudUrl}|${adminKey}`
  if (cached?.signature === signature) return cached.client
  const instance = axios.create({
    baseURL: cloudUrl.replace(/\/+$/, ''),
    timeout: 20_000,
    headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey }
  })
  cached = { signature, client: instance }
  return instance
}

/** Turns anything axios throws into a sentence worth showing a human. */
export function apiErrorMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) {
    return err instanceof Error ? err.message : 'Something went wrong.'
  }

  const status = err.response?.status
  const serverMessage = (err.response?.data as ApiEnvelope<unknown> | undefined)?.message

  // A rejected key is the one failure with an obvious next step, so say what the
  // server said and then point at the place to fix it.
  if (status === 401 || status === 403) {
    const reason = serverMessage ? serverMessage.replace(/\.$/, '') + '. ' : ''
    return reason + 'Admin key rejected — check it in Settings.'
  }

  if (serverMessage) return serverMessage
  if (err.code === 'ECONNABORTED') return 'The server took too long to answer.'
  if (status !== undefined) return `Server returned HTTP ${status}.`
  return 'Cannot reach the server. Check the cloud URL and that it is running.'
}

/**
 * True when a failure says something about the *link* (unreachable host, server
 * fault, rejected key) rather than about the request we sent. A 400 on a
 * duplicate plan name must not turn the connection indicator red.
 */
export function isLinkFailure(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false
  const status = err.response?.status
  if (status === undefined) return true
  return status >= 500 || status === 401 || status === 403
}

/**
 * Calls an admin endpoint and unwraps the `{ message, data }` envelope.
 * Throws the raw axios error so callers can run it through apiErrorMessage.
 */
export async function request<T>(
  config: ApiConfig,
  path: string,
  options: AxiosRequestConfig = {}
): Promise<T> {
  const res = await client(config).request<ApiEnvelope<T>>({ url: path, ...options })
  return res.data?.data as T
}

export const adminApi = {
  listDealers: (c: ApiConfig) => request<import('@/types').Dealer[]>(c, '/admin/dealers'),

  updateDealer: (c: ApiConfig, userId: string, body: import('@/types').UpdateDealerBody) =>
    request<unknown>(c, `/admin/dealers/${userId}`, { method: 'PUT', data: body }),

  deleteDealer: (c: ApiConfig, userId: string) =>
    request<unknown>(c, `/admin/dealers/${userId}`, { method: 'DELETE' }),

  setTrial: (c: ApiConfig, userId: string, days: number) =>
    request<unknown>(c, `/admin/dealers/${userId}/trial`, { method: 'POST', data: { days } }),

  assignPlan: (c: ApiConfig, userId: string, planId: number) =>
    request<unknown>(c, `/admin/dealers/${userId}/assign-plan`, {
      method: 'POST',
      data: { plan_id: planId }
    }),

  revoke: (c: ApiConfig, userId: string) =>
    request<unknown>(c, `/admin/dealers/${userId}/revoke`, { method: 'POST' }),

  listSubDealers: (c: ApiConfig, userId: string) =>
    request<import('@/types').SubDealerListPayload>(c, `/admin/dealers/${userId}/sub-dealers`),

  createSubDealer: (c: ApiConfig, body: import('@/types').CreateSubDealerBody) =>
    request<unknown>(c, '/admin/sub-dealers', { method: 'POST', data: body }),

  updateSubDealer: (c: ApiConfig, userId: string, body: import('@/types').UpdateSubDealerBody) =>
    request<unknown>(c, `/admin/sub-dealers/${userId}`, { method: 'PUT', data: body }),

  deleteSubDealer: (c: ApiConfig, userId: string) =>
    request<unknown>(c, `/admin/sub-dealers/${userId}`, { method: 'DELETE' }),

  listPlans: (c: ApiConfig) => request<import('@/types').Plan[]>(c, '/admin/plans'),

  createPlan: (c: ApiConfig, body: import('@/types').PlanBody) =>
    request<unknown>(c, '/admin/plans', { method: 'POST', data: body }),

  updatePlan: (c: ApiConfig, id: number, body: import('@/types').PlanBody) =>
    request<unknown>(c, `/admin/plans/${id}`, { method: 'PUT', data: body }),

  deletePlan: (c: ApiConfig, id: number) =>
    request<unknown>(c, `/admin/plans/${id}`, { method: 'DELETE' }),

  listExceptions: (c: ApiConfig) =>
    request<import('@/types').ExceptionLog[]>(c, '/admin/exceptions')
}
